import React, { useCallback, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import WarningIcon from '@material-ui/icons/Warning'
import SendIcon from '@material-ui/icons/Send'
import PrintIcon from '@material-ui/icons/Print'
import GetAppIcon from '@material-ui/icons/GetApp'
import VideocamIcon from '@material-ui/icons/Videocam'
import { toast } from 'react-toastify'
import { useStyles } from './styles'
import {
    useMoveOutDetail,
    getMoveOutPdf,
    getFirPdf,
    useSendFmo,
    useSendFir,
} from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { getStatusChip } from './statusUtils'
import { InventoryIssueCard } from './InventoryIssueCard'
import { DamageCard } from './DamageCard'
import { UnpaidChargeCard } from './UnpaidChargeCard'
import { DamageTransactionCard } from './DamageTransactionCard'
import { FinancialDisposition } from './FinancialDisposition'
import { formatPhoneForDisplay } from '../../helpers'
import { formatCurrency, formatDate } from './utils'

interface MoveOutDetailProps {
    open: boolean
    leaseId: number | null
    onClose: () => void
}

export const MoveOutDetail: React.FC<MoveOutDetailProps> = ({
    open,
    leaseId,
    onClose,
}) => {
    const classes = useStyles()
    const { moveOutDetail: detail, isLoading, error } = useMoveOutDetail(
        leaseId,
    )
    const hasFinancialConnection = detail?.entrata_ar_codes_available ?? false

    const totalDamages = parseFloat(detail?.total_damages ?? '0')

    const moveInInspections = detail?.tenant_move_in_inspection ?? []
    const moveOutInspections = detail?.move_out_inspection ?? []

    const moveInInventory = moveInInspections.flatMap((insp) =>
        insp.inventory_inspections
            .filter((inv) => inv.issues.length > 0)
            .map((inv) => ({ ...inv, area_name: insp.area_name })),
    )

    const moveInCompletedDate = moveInInspections
        .map((insp) => insp.completed_date)
        .find((d) => d != null)

    const moveOutInspection =
        moveOutInspections.find((insp) => insp.completed_date != null) ??
        moveOutInspections[0] ??
        null

    const moveOutVideos = moveOutInspections.flatMap((insp) => insp.videos)

    const allDamages = detail?.damages ?? []

    const [pdfLoading, setPdfLoading] = useState(false)
    const [printLoading, setPrintLoading] = useState(false)
    const { mutate: sendFmo, isLoading: sendFmoLoading } = useSendFmo()
    const { mutate: sendFir, isLoading: sendFirLoading } = useSendFir()
    const sendLoading = sendFmoLoading || sendFirLoading

    const getPdf = hasFinancialConnection ? getMoveOutPdf : getFirPdf
    const filePrefix = hasFinancialConnection ? 'FMO' : 'FIR'
    const statementLabel = hasFinancialConnection
        ? 'Final Move-Out Statement'
        : 'Final Inspection Result'

    const handleExportPdf = useCallback(async () => {
        if (!leaseId || !detail) return
        setPdfLoading(true)
        try {
            const blob = await getPdf(leaseId)
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const tenantName = detail.tenant.name.replace(/\s+/g, '-')
            a.download = `${filePrefix}-${detail.lease.unit_name}-${tenantName}.pdf`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Failed to export PDF. Please try again.')
        } finally {
            setPdfLoading(false)
        }
    }, [leaseId, detail, getPdf, filePrefix])

    const handlePrint = useCallback(async () => {
        if (!leaseId) return
        setPrintLoading(true)
        try {
            const blob = await getPdf(leaseId)
            const url = URL.createObjectURL(blob)
            const printWindow = window.open(url)
            if (printWindow) {
                printWindow.addEventListener('load', () => {
                    printWindow.print()
                })
            }
        } catch {
            toast.error('Failed to load PDF for printing.')
        } finally {
            setPrintLoading(false)
        }
    }, [leaseId, getPdf])

    const handleSend = useCallback(() => {
        if (!leaseId) return
        if (hasFinancialConnection) {
            sendFmo(leaseId)
        } else {
            sendFir(leaseId)
        }
    }, [leaseId, hasFinancialConnection, sendFmo, sendFir])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ className: classes.dialogPaper }}
        >
            {/* Header */}
            <Box className={classes.dialogHeader}>
                <Box style={{ width: 40 }} />
                <Box className={classes.dialogHeaderCenter}>
                    <Typography variant="h5" style={{ fontWeight: 700 }}>
                        {statementLabel}
                    </Typography>
                    {detail && (
                        <Box mt={1}>
                            {getStatusChip(
                                detail.lease.final_move_out_status,
                                hasFinancialConnection,
                            )}
                        </Box>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box className={classes.dialogContent}>
                {isLoading && (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={6}
                        style={{ gap: 8 }}
                    >
                        <WarningIcon style={{ color: '#ef4444' }} />
                        <Typography
                            variant="body2"
                            style={{ color: '#ef4444' }}
                        >
                            Failed to load details. Please try again.
                        </Typography>
                    </Box>
                )}

                {detail && !isLoading && !error && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: 24 }}
                    >
                        {/* Lease & Tenant Info */}
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Typography className={classes.sectionTitle}>
                                    Property Information
                                </Typography>
                                <Box className={classes.infoCard}>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {detail.lease.apartment_name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {detail.lease.apartment_address},{' '}
                                        {detail.lease.apartment_city},{' '}
                                        {detail.lease.apartment_state}{' '}
                                        {detail.lease.apartment_zip}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontFamily: 'monospace' }}
                                    >
                                        Unit {detail.lease.unit_name}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={classes.sectionTitle}>
                                    Tenant Information
                                </Typography>
                                <Box className={classes.infoCard}>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {detail.tenant.name}
                                    </Typography>
                                    {detail.tenant.email && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {detail.tenant.email}
                                        </Typography>
                                    )}
                                    {detail.tenant.phone && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {formatPhoneForDisplay(
                                                detail.tenant.phone,
                                            )}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Dates Row */}
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Box className={classes.dateCard}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Lease Start
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {formatDate(detail.lease.start_date)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box className={classes.dateCard}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Move-Out
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {formatDate(
                                            detail.lease.move_out_date ||
                                                detail.lease.end_date,
                                        )}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box className={classes.dateCard}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Inspection Complete
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {moveOutInspection?.completed_date
                                            ? formatDate(
                                                  moveOutInspection.completed_date,
                                              )
                                            : 'Not completed'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Move In Inspection */}
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 12 }}
                            >
                                <Typography
                                    className={classes.sectionTitle}
                                    style={{ marginBottom: 0 }}
                                >
                                    Move In Inspection
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {moveInCompletedDate
                                        ? `Completed ${formatDate(
                                              moveInCompletedDate,
                                          )}`
                                        : 'Not completed'}
                                </Typography>
                            </Box>

                            {moveInInventory.length > 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    style={{ gap: 12 }}
                                >
                                    {moveInInventory.map((inv) => (
                                        <InventoryIssueCard
                                            key={inv.id}
                                            inventoryInspection={inv}
                                            areaName={inv.area_name}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Box className={classes.infoCard}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No issues found in the move-in
                                        inspection.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Move Out Inspection */}
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 12 }}
                            >
                                <Typography
                                    className={classes.sectionTitle}
                                    style={{ marginBottom: 0 }}
                                >
                                    Move Out Inspection
                                </Typography>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 12 }}
                                >
                                    {moveOutInspection?.completed_by && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            By {moveOutInspection.completed_by}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {moveOutInspection?.completed_date
                                            ? `Completed ${formatDate(
                                                  moveOutInspection.completed_date,
                                              )}`
                                            : 'Not completed'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Move-Out Video */}
                            {moveOutVideos.length > 0 && (
                                <Box
                                    className={classes.videoSection}
                                    style={{ marginBottom: 16 }}
                                >
                                    <Box className={classes.videoHeader}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8 }}
                                        >
                                            <VideocamIcon
                                                style={{ fontSize: 18 }}
                                            />
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Inspection Video
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box style={{ backgroundColor: '#000' }}>
                                        <video
                                            controls
                                            style={{
                                                width: '100%',
                                                maxHeight: 400,
                                                display: 'block',
                                            }}
                                        >
                                            <source
                                                src={moveOutVideos[0].url}
                                            />
                                        </video>
                                    </Box>
                                </Box>
                            )}

                            {/* Damages */}
                            {allDamages.length > 0 ? (
                                <Box className={classes.damageSection}>
                                    <Box className={classes.damageHeader}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8 }}
                                        >
                                            <WarningIcon
                                                style={{
                                                    fontSize: 18,
                                                    color: '#ef4444',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    color: '#ef4444',
                                                }}
                                            >
                                                Damages ({allDamages.length})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {allDamages.map((damage) => (
                                        <DamageCard
                                            key={damage.id}
                                            damage={damage}
                                            areaName={damage.area_name ?? ''}
                                        />
                                    ))}
                                    {hasFinancialConnection &&
                                        (detail.damage_transactions ?? [])
                                            .length > 0 && (
                                            <Box>
                                                <Box
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor:
                                                            'rgba(239, 68, 68, 0.04)',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            fontWeight: 600,
                                                            color: '#991b1b',
                                                            textTransform:
                                                                'uppercase',
                                                            letterSpacing:
                                                                '0.05em',
                                                        }}
                                                    >
                                                        Damage Transactions (
                                                        {
                                                            detail.damage_transactions!
                                                                .length
                                                        }
                                                        )
                                                    </Typography>
                                                </Box>
                                                {detail.damage_transactions!.map(
                                                    (txn, idx) => (
                                                        <DamageTransactionCard
                                                            key={idx}
                                                            transaction={txn}
                                                        />
                                                    ),
                                                )}
                                            </Box>
                                        )}
                                    <Box className={classes.damageTotal}>
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Total Damages
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontWeight: 700,
                                                color: '#ef4444',
                                            }}
                                        >
                                            {formatCurrency(totalDamages)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box className={classes.infoCard}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No damages recorded.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {hasFinancialConnection ? (
                            <FinancialDisposition detail={detail} />
                        ) : (
                            <Box className={classes.financialNoticeCard}>
                                <WarningIcon
                                    style={{
                                        color: '#f59e0b',
                                        fontSize: 22,
                                        flexShrink: 0,
                                        marginTop: 2,
                                    }}
                                />
                                <Box>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            fontWeight: 600,
                                            color: '#92400e',
                                            marginBottom: 4,
                                        }}
                                    >
                                        Financial Data Unavailable
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ color: '#92400e' }}
                                    >
                                        Financial disposition details (deposit,
                                        past due balance, and refund
                                        calculation) are unavailable. This
                                        property has not connected Entrata
                                        financial data.
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <Box className={classes.actionBar}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={
                                    sendLoading ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SendIcon />
                                    )
                                }
                                style={{ flex: 1 }}
                                onClick={handleSend}
                                disabled={sendLoading}
                            >
                                {sendLoading
                                    ? 'Sending...'
                                    : `Send ${filePrefix} to Tenant`}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={
                                    printLoading ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <PrintIcon />
                                    )
                                }
                                onClick={handlePrint}
                                disabled={printLoading}
                            >
                                {printLoading ? 'Loading...' : 'Print'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={
                                    pdfLoading ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <GetAppIcon />
                                    )
                                }
                                onClick={handleExportPdf}
                                disabled={pdfLoading}
                            >
                                {pdfLoading ? 'Exporting...' : 'Export PDF'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    )
}
